// System directives

"use client";

// System dependencies

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { useRouter } from "next/navigation";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

// Application dependencies

import FormInputPassword from "@/components/form/formInputPassword/FormInputPassword";
import FormInputText from "@/components/form/formInputText/FormInputText";
import FormSpinnerPage, { FormSpinnerPageProps } from "@/components/form/formSpinner/FormSpinnerPage";
import FormThemeSwitcher from "@/components/form/formThemeSwitcher/FormThemeSwitcher";
import NavLink from "@/components/nav/auth/navLink/NavLink";
import { AuthFields, AuthProviders, AuthWaitingStatus, AuthWaitingType } from "@/_auth/interfaces/auth.interfaces";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { FormInputCase } from "@/interfaces/formInput.interfaces";
import { NAV_HEADER_NAME } from "@/interfaces/nav.interfaces";
import { RouteApis, RouteFields, RouteNames } from "@/interfaces/route.interfaces";
import { SignInSchema } from "@/schemas/authorisationSchema";
import { clearFormSpinner, formatErrorMessage, manageFormSpinner, manageFormSpinnerPage } from "@/lib/functions";
import { getAuthSelector } from "@/_auth/redux/auth/auth.slices";
import { getAuthUserByEmailAction } from "@/_auth/actions/auth.actions";
import { supabaseClient } from "@/_auth/utils/supabase/client";
import { useAppSelector } from "@/redux/store.hooks";

// Styling

import { FaGoogle } from "react-icons/fa";

// Namespace

//const NAMESPACE = "signin";

// devConsole.info(NAMESPACE,``);

const SignInPage = () => {
  // Application state

  const [spinnerPageState, setSpinnerPageState] = useState<FormSpinnerPageProps | null>(null);
  const router = useRouter();

  // Redux state

  const auth = useAppSelector(getAuthSelector);

  // ---------------------------------------------------------------------------------------------------------------------------------------
  // Use effect to handle the form spinner control on component mount
  // ---------------------------------------------------------------------------------------------------------------------------------------

  useEffect(() => {
    clearFormSpinner();
  }, []);

  // ---------------------------------------------------------------------------------------------------------------------------------------
  // UseEffect to handle the authentication of the user after an email/password entry. It is designed to overcome the race issue we were
  // experiencing with the router.push to a private page before the user is authenticated.
  // ---------------------------------------------------------------------------------------------------------------------------------------

  useEffect(() => {
    // Check to see whether there is an auth user.

    if (auth[AuthFields.User]) {
      // Turn off the page spinner.

      manageFormSpinnerPage(null, setSpinnerPageState);

      // Start a form spinner.

      manageFormSpinner(`loading ${RouteNames[RouteFields.Dashboard].name} page - please wait`);

      // And push to the dashboard.
      router.push(RouteNames[RouteFields.Dashboard].route);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  // ---------------------------------------------------------------------------------------------------------------------------------------
  // React Hook Forms management
  // ---------------------------------------------------------------------------------------------------------------------------------------

  // Set the Form instance and submit handler

  const signInForm = useForm<z.infer<typeof SignInSchema>>({
    defaultValues: {
      [AuthFields.Email]: "",
      [AuthFields.Password]: "",
    },
    mode: "onChange",
    resolver: zodResolver(SignInSchema),
  });

  // Submit record

  const onSignInSubmit = signInForm.handleSubmit(async (values: z.infer<typeof SignInSchema>) => {
    // Run up the form spinner page.

    manageFormSpinnerPage("attempting signin", setSpinnerPageState);

    // Try and sign in.

    try {
      // Try and get a user for the email presented.

      const { response } = await getAuthUserByEmailAction(values[AuthFields.Email].toLowerCase());

      // See whether we have an error.

      if (response[AuthFields.Status] !== 200) {
        // To be here there is an error, put the message into a toast, clear the form spinner page and return.

        toast.error(`error experienced - ${response[AuthFields.Message]}`);
        manageFormSpinnerPage(null, setSpinnerPageState);
        return;
      }

      // Make sure that we've found a matching record.

      if (!response[AuthFields.Id]) {
        // To be here there is no matching record. Put the message into a toast, clear the form spinner page and return.
        toast.error("this email does not exist, please sign up instead");
        manageFormSpinnerPage(null, setSpinnerPageState);
        return;
      }

      // Try and sign in with the entered credentials.

      const { error } = await supabaseClient.auth.signInWithPassword({
        [AuthFields.Email]: values[AuthFields.Email].toLowerCase(),
        [AuthFields.Password]: values[AuthFields.Password],
      });

      // Check for an error.

      if (error) {
        // To be here we have an error, put the error into a toast and turn off the form spinner page.

        toast.error(formatErrorMessage(error));
        manageFormSpinnerPage(null, setSpinnerPageState);
      }

      // Note that if we've been successful we just return leaving the spinner running as we need to wait for the use effect watching
      // the user to fire to counter the race issue with Supabase loading the user authentication after we route to a protected page.
    } catch (error) {
      // There has been an unexpcted error. Display a toast with the error and turn off the form spinner page.

      toast.error(formatErrorMessage(error));
      manageFormSpinnerPage(null, setSpinnerPageState);
    }
  });

  // ---------------------------------------------------------------------------------------------------------------------------------------
  // Form interaction and rendering
  // ---------------------------------------------------------------------------------------------------------------------------------------

  const signInReset = () => {
    signInForm.reset();
  };

  const signInGoogle = async () => {
    // Run up the form spinner page.

    manageFormSpinnerPage("attempting Google signin", setSpinnerPageState);

    // Try and sign in.

    try {
      // Set the redirectTo value.

      const redirectTo = `${process.env.NEXT_PUBLIC_APP_URL}/api/auth/callbacks${RouteApis.oauth}?${AuthFields.Provider}=${AuthWaitingType.Oauth_google}`;

      // Try and signin with OAuth.

      const { error } = await supabaseClient.auth.signInWithOAuth({
        provider: AuthProviders.Google,
        options: {
          redirectTo,
        },
      });

      // Check for an error.

      if (error) {
        // To be here we have an error, put the error into a toast, turn off the form spinner page and return.

        toast.error(formatErrorMessage(error));
        manageFormSpinnerPage(null, setSpinnerPageState);
        return;
      }

      // Load the waiting page ready for the return from google.

      manageFormSpinnerPage(`loading ${RouteNames[RouteFields.Waiting].name} page`, setSpinnerPageState);
      router.push(
        `${process.env.NEXT_PUBLIC_APP_URL}${RouteNames[RouteFields.Waiting].route}?${AuthFields.Type}=${AuthWaitingType.Oauth_google}&${AuthFields.Status}=${AuthWaitingStatus.Waiting}`,
      );
    } catch (error) {
      // Handle any unexpected error.

      toast.error(formatErrorMessage(error));
      manageFormSpinnerPage(null, setSpinnerPageState);
    }
  };

  return (
    <div className="relative flex h-full min-h-screen w-full flex-col justify-between bg-white px-4 py-6 dark:bg-slate-800 dark:text-white xsm:px-10 sm:h-full sm:w-full md:h-full md:w-3/5 lg:h-full lg:w-2/5 xl:h-full 3xl:px-7p 4xl:px-10p">
      <div>
        {spinnerPageState && <FormSpinnerPage isActive={spinnerPageState.isActive} caption={spinnerPageState.caption} />}
        {/* Logo */}
        <span onClick={() => manageFormSpinner(`loading ${RouteNames[RouteFields.Home].name} page - please wait`)}>
          <Link className="flex cursor-pointer space-x-3 whitespace-nowrap focus:outline-0" href={RouteNames[RouteFields.Home].route}>
            <Image alt="Logo" className="h-auto w-auto" height={28} src={"assets/logo-icon.svg"} width={28} />
            <span className="self-center whitespace-nowrap text-xl md:text-2xl">
              <span className="text-logoblue dark:text-logoblue-300">{NAV_HEADER_NAME.Prefix}</span>
              <span className="font-semibold">{NAV_HEADER_NAME.Suffix}</span>
            </span>
          </Link>
        </span>
        {/* Content */}
        <div className="flex w-full flex-col">
          <div className="mt-[45px] w-full">
            {/* Heading */}
            <div className="mb-4 flex w-full flex-col">
              <h3 className="mb-2 text-2xl font-semibold">Sign In</h3>
              <p className="mb-2 text-base">Welcome Back - please enter your details</p>
            </div>
            {/* Form */}
            <Form {...signInForm}>
              <form id="signin" noValidate onSubmit={onSignInSubmit}>
                <div className="flex w-full flex-col">
                  <FormInputText
                    control={signInForm.control}
                    inputCase={FormInputCase.Lowercase}
                    label="email"
                    name="email"
                    placeholder="enter email address"
                  />
                </div>
                <div className="mt-4 flex w-full flex-col">
                  <FormInputPassword control={signInForm.control} label="password" name="password" placeholder="enter your password" />
                </div>
                <div className="flex w-full items-center justify-between pt-2">
                  <NavLink displaySpinner={false} label="Reset" onClick={signInReset} />
                  <NavLink
                    displaySpinner={true}
                    href={RouteNames[RouteFields.Forgot].route}
                    label="Forgot password?"
                    pageName={RouteNames[RouteFields.Forgot].name}
                  />
                </div>
                <div className="my-5 flex w-full flex-col">
                  <Button className="my-2 w-full" type="submit">
                    Sign in
                  </Button>
                </div>
                <div className="relative flex w-full items-center justify-center py-2">
                  <div className="h-[1px] w-full bg-black dark:bg-white" />
                  <p className="absolute bg-white px-2 text-black/80 dark:bg-slate-800 dark:text-white/80"> or </p>
                </div>
                <div className="mt-6 w-full">
                  <Button className="w-full bg-googleblue text-white hover:bg-googleblue" onClick={() => signInGoogle()} type="button">
                    <FaGoogle className="mr-2" />
                    Sign in with Google
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </div>
      </div>
      {/* Footer */}
      <div className="flex w-full items-center justify-between">
        <span className="flex text-sm font-normal text-gray-700 dark:text-gray-400">
          {"Don't have an account?"}
          <span className="ml-1">
            <NavLink
              displaySpinner={true}
              href={RouteNames[RouteFields.SignUp].route}
              label="Sign up here"
              pageName={RouteNames[RouteFields.SignUp].name}
            />
          </span>
        </span>
        <FormThemeSwitcher />
      </div>
    </div>
  );
};

export default SignInPage;
